/**
 * Audio utilities for WAV file creation and audio data conversion
 */

/**
 * Converts Float32Array audio data to 16-bit PCM Int16Array
 */
export const floatTo16BitPCM = (float32Array: Float32Array): Int16Array => {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16Array;
};

/**
 * Creates a WAV blob from Float32Array audio data
 */
export const createWavBlob = (
  audioData: Float32Array,
  sampleRate: number = 16000
): Blob => {
  const int16Data = floatTo16BitPCM(audioData);
  const buffer = new ArrayBuffer(44 + int16Data.length * 2);
  const view = new DataView(buffer);

  // Write WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + int16Data.length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // Mono channel
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // Byte rate
  view.setUint16(32, 2, true); // Block align
  view.setUint16(34, 16, true); // Bits per sample
  writeString(36, "data");
  view.setUint32(40, int16Data.length * 2, true);

  // Write PCM data
  const dataOffset = 44;
  for (let i = 0; i < int16Data.length; i++) {
    view.setInt16(dataOffset + i * 2, int16Data[i], true);
  }

  return new Blob([buffer], { type: "audio/wav" });
};

